import { getGlobalSelectionModeSelector } from "components/advertising/logic/sagaHelpers";
import { advertisingListSelector, advertisingSelectedINListSelector } from "core/dataConstants";
import {createError, handleServerErrors} from "core/exceptions";
import { assertArray, assertObject, assertString } from "core/validation";
import { cloneDeep, uniqBy } from "lodash";
import log from "loglevel";
import { toast } from "react-toastify";
import { call, fork, put, select, takeLatest } from "redux-saga/effects";
import AdvertisingService from "services/advertisingService";
import ReportService from "services/reportService";
import envconfig from "../../../../envconfig";
import { actions as pdfActions } from "../../../fileViewers/logic/pdfActions";
import { advertiserAddedSuccess, advertiserFailedAdvRepited, filterUpAdv } from "../../common";
import {
    actions,
    advInLogicErrorSelector,
    advToReportSelector,
    advertisersSelector,
    filmReleaseInLogicErrorSelector,
    filmReleaseSelector,
} from "./advReportActions";

export function* addAdvToReport({ payload }) {
    try {
        const advertisers = yield select(advertisersSelector);
        const toAdd = assertArray(payload) ? filterUpAdv(payload, advertisers) : [];
        let filmRelease = yield select(filmReleaseSelector);
        yield call(addToReport, filmRelease, toAdd, advertisers);
    } catch (e) {
        log.error(e);
    }
}

export function* addAdvToReportById({ payload }) {
    try {
        const advertisers = yield select(advertisersSelector);
        const advData = yield call(AdvertisingService.getAdv, payload.id);
        if (advData && advData.data) {
            const adv = advData.data;
            const toAdd = filterUpAdv([adv], advertisers);
            let filmRelease = yield select(filmReleaseSelector);
            yield call(addToReport, filmRelease, toAdd, advertisers);
        }
    } catch (e) {
        log.error(e);
    }
}

export function* addGlobalAdvReport() {
    try {
        const selectionMode = yield select(getGlobalSelectionModeSelector);

        let list = [];
        if (selectionMode === 1) {
            list = yield select(advertisingListSelector);
        } else if (selectionMode === -1) {
            list = yield select(advertisingSelectedINListSelector);
        }
        if (assertArray(list)) {
            const advertisers = yield select(advertisersSelector);
            const toAdd = assertArray(list) ? filterUpAdv(list, advertisers) : [];
            let filmRelease = yield select(filmReleaseSelector);
            yield call(addToReport, filmRelease, toAdd, advertisers);
            yield put(actions.ON_ADD_GLOBAL_REPORT.success());
        }
    } catch (e) {
        log.error(e);
        yield put(actions.ON_ADD_GLOBAL_REPORT.failed(null, createError(e)));
    }
}

export function* addToReport(filmRelease, toAdd, advertisers) {
    try {
        let invalidFilmRelease = null;
        log.debug("adding adv to report", filmRelease, advertisers, toAdd);
        let _advertisers = [];
        if (assertArray(toAdd)) {
            if (assertObject(filmRelease)) {
                invalidFilmRelease = toAdd[0].filmRelease.id !== filmRelease.id ? toAdd[0].filmRelease : null;
            } else {
                filmRelease = cloneDeep(toAdd[0].filmRelease);
                invalidFilmRelease = null;
            }
            _advertisers = uniqBy([...advertisers, ...toAdd], (a) => a.id);
        }
        if (!assertObject(invalidFilmRelease) && assertArray(toAdd)) {
            yield call(addAdvertisers, filmRelease, _advertisers, _advertisers.length, toAdd.length);
        } else {
            if (!assertArray(toAdd) && !assertObject(invalidFilmRelease)) {
                advertiserFailedAdvRepited();
            }

            yield put(
                actions.ON_ADD_ADV_TO_REPORT.failed({
                    logicError: {
                        invalidFilmRelease: cloneDeep(invalidFilmRelease),
                        advToAdd: cloneDeep(toAdd),
                        advExist: !assertArray(toAdd),
                    },
                })
            );
        }
    } catch (error) {
        log.error(error);
    }
}

export function* addAdvertisers(filmRelease, advertisers, advertisersCount, toAddCount) {
    try {
        const advertisementTypes = advertisers.map((a) => ({
            ...a.advertisementType,
            value: a.advertisementType.id,
            label: a.advertisementType.name,
        }));
        const payloadResponse = {
            filmRelease: cloneDeep(filmRelease),
            advertisers: cloneDeep(advertisers),
            advToReport: cloneDeep(advertisers),
            advertisersCount: advertisersCount,
            advertisementTypes: uniqBy(advertisementTypes, (e) => e.id),
        };

        yield put(actions.ON_ADD_ADV_TO_REPORT.success(payloadResponse));
        yield fork(advertiserAddedSuccess, toAddCount, "Advertising report");
    } catch (error) {
        log.error(error);
    }
}

export function* onLoadAdvReport(action) {
    try {
        const loadAds = action.payload && action.payload.loadAds;
        const filmR = yield select(filmReleaseSelector);
        let advertisers = yield select(advertisersSelector);
        if (loadAds && assertArray(advertisers)) {
            const ids = advertisers.map((a) => a.id).join(",");
            const advCall = yield call(AdvertisingService.getAdvsByIds, ids);
            if (advCall) {
                advertisers = advCall.data;
            }
        }
        const filmReleaseId = filmR.id;
        const filmresponse = yield call(AdvertisingService.getFilmReleaseById, filmReleaseId);
        yield put(
            actions.ON_LOAD_REPORT_VIEW.success({
                filmRelease: filmresponse.data,
                advertisers,
                advToReport: cloneDeep(advertisers),
            })
        );
    } catch (e) {
        log.error(e);
        yield put(actions.ON_ADD_ADV_TO_REPORT.failed(null, createError(e)));
    }
}

export function* onPreviewPdf({ payload }) {
    try {
        const { date, primaryContactId, primaryContactName, comment } = payload;
        const adv = yield select(advToReportSelector);
        const advList = adv.map((a) => a.id).join(",");
        let url = `${envconfig.api}/report/adv?advList=${advList}`;
        if(date) {
            url += `&date=${date}`;
        }
        if (assertObject(primaryContactId)) {
            url += `&primaryContactId=${primaryContactId}`;
        } else if (assertString(primaryContactName)) {
            url += `&primaryContactName=${primaryContactName}`;
        }
        if (assertString(comment)) {
            url += `&comment=${comment}`;
        }
        yield put(
            pdfActions.ON_OPEN_PDF.call({
                url,
            })
        );
    } catch (e) {
        log.error(e);
    }
}

export function* onResetReportWithAdv() {
    try {
        const advertisers = yield select(advInLogicErrorSelector);
        const filmRelease = yield select(filmReleaseInLogicErrorSelector);
        yield call(addAdvertisers, filmRelease, advertisers, advertisers.length, advertisers.length);
        yield put(actions.ON_CLEAR_REPORT_ERROR.call());
    } catch (e) {
        log.error(e);
    }
}

export function* sendEmail({ payload }) {
    try {
        const { date, primaryContactId, primaryContactName, comment, message } = payload;
        const adv = yield select(advToReportSelector);
        const advList = adv.map((a) => a.id);
        const data = {
            ...message,
            report: {
                date,
                primaryContactId,
                primaryContactName,
                advList,
                comment: assertString(comment) ? comment : null,
            },
        };
        yield call(ReportService.sendAdvReport, data);
        yield put(actions.ON_SEND_REPORT.success());
        toast.info("The report has been sent", {
            position: "top-right"
        });
    } catch (e) {
        handleServerErrors(e, () => {
            toast.error("Error Sending email. Please contact the administrator", {
                position: "top-center"
            });
        });
        log.error(e);
        yield put(actions.ON_SEND_REPORT.failed(null, createError(e)));
    }
}

export default [
    takeLatest(actions.ON_ADD_ADV_TO_REPORT.sendType, addAdvToReport),
    takeLatest(actions.ON_LOAD_REPORT_VIEW.sendType, onLoadAdvReport),
    takeLatest(actions.ON_PREVIEW_REPORT.type, onPreviewPdf),
    takeLatest(actions.ON_ADD_GLOBAL_REPORT.sendType, addGlobalAdvReport),
    takeLatest(actions.ON_CLEAR_WITH_NEW_FILM_RELEASE.type, onResetReportWithAdv),
    takeLatest(actions.ON_SEND_REPORT.sendType, sendEmail),
    takeLatest(actions.ON_ADD_ADV_TO_REPORT_BY_ID.type, addAdvToReportById),
];
